import { union } from 'lodash-es';

import { getSortedPanelChildren } from './panels.js';
import { getSortedResizeHandles, getResizeHandle } from './resize-handles.js';
import { RESIZE_HANDLE_SIZE } from '../Resizer.js';
import {
  ResizeHandles,
  ResizeHandle,
  Panels,
  PanelSizesWithUnit,
  PanelChildren,
} from '../types/shared-panel-types.js';

/**
 * Generate grid template columns for all panels and resize handles.
 * If no sizes are provided, fall back to 1fr for the max column size.
 * This is needed so we can calculate the sizes correctly in case the
 * minWidth of all panels exceeds the parent panel width.
 */
export function generateGridTemplateColumns(
  sortedPanelChildren: PanelChildren,
  sortedResizeHandles: ResizeHandles,
  sizes?: PanelSizesWithUnit,
) {
  return sortedPanelChildren.reduce((template, panel, index) => {
    const panelSize =
      sizes?.[panel.name].value !== undefined ? sizes?.[panel.name].value : 1;
    const panelSizeUnit = sizes?.[panel.name].unit || 'fr';

    const panelTemplateColumn =
      sizes?.[panel.name].value === 0
        ? // To be able to animate the columns, the from and to column's list lenghts
          // must match, by computed value type per item -> we can't animate
          // from 0px to minmax(...) but must always use minmax(...)
          'minmax(0px, 0fr)'
        : `minmax(${panel.minWidth || 0}px, ${panelSize}${panelSizeUnit})`;

    const resizeHandle = getResizeHandle(panel, sortedResizeHandles);
    const resizeHandleIndex = sortedResizeHandles.findIndex(
      (handle) => handle === resizeHandle,
    );
    const resizeHandleColumn =
      resizeHandle !== null
        ? `${sizes?.[panel.name].value === 0 ? '0' : RESIZE_HANDLE_SIZE}px`
        : undefined;

    const additionalColumns = [panelTemplateColumn, resizeHandleColumn].filter(
      (column) => column,
    );

    return `${template} ${
      // To match the position in the DOM, we need to add the resize handle column either to left
      // or the right of the panel's column, depending on the indices of the corresponding panel
      // resize handle in the sorted arrays.
      resizeHandleIndex === index
        ? additionalColumns.join(' ')
        : additionalColumns.reverse().join(' ')
    }`;
  }, '');
}

/**
 * Generate grid template areas for all panels and resize handles.
 * Example of returned template areas:
 *    "header   header                 header"
 *    "sidebar  sidebar-resize-handle  main  "
 *
 * At the moment, the "header" area is mainly used for the page component, so
 * we can render its compounds anywhere and they still end up in the right place.
 */
export function generateGridTemplateAreas(
  sortedPanelChildren: PanelChildren,
  sortedResizeHandles: ResizeHandles,
) {
  return `"${Array(sortedPanelChildren.length + sortedResizeHandles.length)
    .fill('header')
    .join(' ')}" "${sortedPanelChildren.reduce(
    (template, panel, index) =>
      `${template} ${panel.name} ${sortedResizeHandles[index]?.name || ''}`,
    '',
  )}"`;
}

/**
 * Generate all the states of the grid template columns and grid template areas
 * needed to animate added / removed panels.
 */
export function getAnimationStates(
  sortedPanels: PanelChildren,
  prevPanels: PanelChildren,
  sortedResizeHandles: ResizeHandles,
  prevResizeHandles: ResizeHandles,
  newSizesFinal: PanelSizesWithUnit,
  newSizesAnimationStart: PanelSizesWithUnit | undefined,
  newSizesAnimationEnd: PanelSizesWithUnit | undefined,
) {
  // In order to calculate the template columns and areas correctly when starting the
  // transition, we need to combine the previous and current resize handles / panels.
  // e.g. panel has been removed -> we still have to include it in the
  // columns / areas until the transition is done.
  const combinedResizeHandles = getSortedResizeHandles(
    union([...prevResizeHandles, ...sortedResizeHandles]).reduce(
      (acc: Record<string, ResizeHandle>, entry) => {
        acc[entry.name] = entry;
        return acc;
      },
      {},
    ),
  );
  const combinedPanelChildren = getSortedPanelChildren(
    union([...prevPanels, ...sortedPanels]).reduce((acc: Panels, entry) => {
      acc[entry.name] = entry;
      return acc;
    }, {}),
  );

  // Re-generate grid template areas
  const gridTemplateAreas = generateGridTemplateAreas(
    sortedPanels,
    sortedResizeHandles,
  );
  const gridTemplateAreasStart = generateGridTemplateAreas(
    combinedPanelChildren,
    combinedResizeHandles,
  );

  // Re-generate grid template columns
  const gridTemplateColumns = generateGridTemplateColumns(
    sortedPanels,
    sortedResizeHandles,
    newSizesFinal,
  );
  const gridTemplateColumnsStart = generateGridTemplateColumns(
    combinedPanelChildren,
    combinedResizeHandles,
    newSizesAnimationStart,
  );
  const gridTemplateColumnsEnd = generateGridTemplateColumns(
    combinedPanelChildren,
    combinedResizeHandles,
    newSizesAnimationEnd,
  );

  return {
    gridTemplateAreas,
    gridTemplateAreasStart,
    gridTemplateColumns,
    gridTemplateColumnsStart,
    gridTemplateColumnsEnd,
  };
}
